import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * Native Horizon cart drawer controller.
 *
 * Restores:
 * - auto-open after Add to Cart
 * - quick-add/modal-safe opening
 * - native theme-drawer slide behavior
 * - sticky-summary recalculation
 *
 * No extra cart request is created here.
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (
      this.closest('theme-drawer')
    );
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.addEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );

    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.removeEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );
  }

  #handleDrawerOpen = () => {
    this.#updateStickyState();

    customElements
      .whenDefined('shopify-payment-terms')
      .then(() => {
        const cta = this
          .querySelector('shopify-payment-terms')
          ?.shadowRoot
          ?.querySelector('#shopify-installments-cta');

        cta?.addEventListener(
          'click',
          () => this.#themeDrawer?.close(),
          { once: true }
        );
      });
  };

  /**
   * Auto-open the drawer for every native Horizon cart add.
   *
   * - Existing cart: open immediately.
   * - Empty cart: wait until the cart-add promise resolves so fresh
   *   drawer content is available.
   * - Quick Add modal: wait for that modal to close first.
   *
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen =
      this.hasAttribute('auto-open') &&
      event.action === 'add' &&
      !this.#themeDrawer?.isOpen;

    const sourceModal =
      /** @type {HTMLDialogElement | null} */ (
        event.target instanceof Element
          ? event.target.closest('dialog:modal')
          : null
      );

    if (
      shouldAutoOpen &&
      !sourceModal &&
      !this.#isCartEmpty()
    ) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => {
          requestAnimationFrame(() => {
            this.#updateStickyState();
          });
        };

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) {
            this.#themeDrawer?.open();
          }

          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener(
            'close',
            openAndSettle,
            { once: true }
          );
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[cart-drawer] Event promise rejected:',
            error
          );
        }
      });
  };

  #isCartEmpty() {
    return Boolean(
      this.querySelector('.cart-drawer--empty')
    );
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    const content =
      dialog.querySelector('.cart-drawer__content');

    const summary =
      dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute(
        'cart-summary-sticky',
        'false'
      );
      return;
    }

    const drawerHeight =
      dialog.getBoundingClientRect().height;

    const summaryHeight =
      summary.getBoundingClientRect().height;

    const ratio =
      summaryHeight / drawerHeight;

    dialog.setAttribute(
      'cart-summary-sticky',
      ratio > this.#summaryThreshold
        ? 'false'
        : 'true'
    );
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define(
    'cart-drawer-component',
    CartDrawerComponent
  );
}


/**
 * implicit™ cart UI enhancements.
 *
 * Keeps your current:
 * - coupon panel interactions
 * - T&C checkout state
 * - recommendation loader
 * - recommendation Add button
 *
 * This is intentionally separate from CartDrawerComponent so the
 * native Horizon drawer lifecycle remains intact.
 */
class ImplicitCartUI extends HTMLElement {
  #abort = new AbortController();

  connectedCallback() {
    this.#abort.abort();
    this.#abort = new AbortController();

    const signal = this.#abort.signal;

    this.querySelector(
      '[data-implicit-open-coupons]'
    )?.addEventListener(
      'click',
      () => this.#toggleCoupons(true),
      { signal }
    );

    this.querySelector(
      '[data-implicit-close-coupons]'
    )?.addEventListener(
      'click',
      () => this.#toggleCoupons(false),
      { signal }
    );

    this.querySelector(
      '[data-implicit-apply]'
    )?.addEventListener(
      'click',
      (event) => {
        const button =
          /** @type {HTMLElement} */ (
            event.currentTarget
          );

        this.#applyCoupon(
          button.dataset.implicitApply || ''
        );
      },
      { signal }
    );

    this.querySelectorAll(
      '[data-implicit-panel-apply]'
    ).forEach((button) => {
      button.addEventListener(
        'click',
        () => {
          if (button instanceof HTMLElement) {
            this.#applyCoupon(
              button.dataset
                .implicitPanelApply || ''
            );
          }
        },
        { signal }
      );
    });

    const policy =
      this.querySelector(
        '[data-implicit-policy]'
      );

    const checkout =
      this.querySelector(
        '[data-implicit-checkout]'
      );

    if (
      policy instanceof HTMLInputElement &&
      checkout instanceof HTMLButtonElement
    ) {
      checkout.disabled = !policy.checked;

      policy.addEventListener(
        'change',
        () => {
          checkout.disabled =
            !policy.checked;
        },
        { signal }
      );
    }

    const fallbackAdd =
      this.querySelector(
        '[data-implicit-add-related][data-variant-id]'
      );

    if (
      fallbackAdd instanceof HTMLButtonElement
    ) {
      fallbackAdd.addEventListener(
        'click',
        () => this.#addRelated(
          fallbackAdd.dataset.variantId,
          fallbackAdd
        ),
        { signal }
      );
    }

    this.#loadRecommendation();
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  #toggleCoupons(open) {
    const panel =
      this.querySelector(
        '[data-implicit-coupon-panel]'
      );

    if (!(panel instanceof HTMLElement)) {
      return;
    }

    panel.classList.toggle(
      'is-open',
      open
    );

    panel.setAttribute(
      'aria-hidden',
      String(!open)
    );

    if (open) {
      requestAnimationFrame(() => {
        this.querySelector(
          '#implicit-cart-discount'
        )?.focus();
      });
    }
  }

  #applyCoupon(code) {
    if (!code) return;

    const input =
      this.querySelector(
        '#implicit-cart-discount'
      );

    const form =
      input?.closest('form');

    if (
      !(input instanceof HTMLInputElement) ||
      !(form instanceof HTMLFormElement)
    ) {
      return;
    }

    input.value = code;
    form.requestSubmit();
    this.#toggleCoupons(false);
  }

  async #loadRecommendation() {
    const section =
      this.querySelector(
        '[data-implicit-recommendations]'
      );

    const mount =
      this.querySelector(
        '[data-implicit-recommendation-card]'
      );

    const productId =
      this.dataset.recommendationProductId;

    if (
      !(section instanceof HTMLElement) ||
      !(mount instanceof HTMLElement) ||
      !productId
    ) {
      return;
    }

    try {
      const root =
        window.Shopify?.routes?.root || '/';

      const url =
        `${root}recommendations/products.json` +
        `?product_id=${encodeURIComponent(productId)}` +
        `&limit=4&intent=related`;

      const response =
        await fetch(url, {
          headers: {
            Accept: 'application/json'
          },
          signal: this.#abort.signal
        });

      if (!response.ok) return;

      const data =
        await response.json();

      const inCart =
        new Set(
          (
            this.dataset.cartProductIds || ''
          ).split(',')
        );

      const product =
        data.products?.find(
          (item) =>
            item.available &&
            !inCart.has(String(item.id))
        );

      const variant =
        product?.variants?.find(
          (item) => item.available
        );

      if (!product || !variant) return;

      const currency =
        window.Shopify?.currency?.active ||
        'INR';

      const price =
        new Intl.NumberFormat(
          'en-IN',
          {
            style: 'currency',
            currency,
            maximumFractionDigits: 0
          }
        ).format(
          variant.price / 100
        );

      const image =
        product.featured_image ||
        product.images?.[0] ||
        '';

      mount.innerHTML = `
        <article class="implicit-rec-card">
          ${
            image
              ? `<img src="${this.#escape(image)}?width=148" width="74" height="74" loading="lazy" decoding="async" alt="">`
              : '<span></span>'
          }
          <div>
            <h4>${this.#escape(product.title)}</h4>
            <p>${price}</p>
          </div>
          <button type="button" data-implicit-add-related>Add</button>
        </article>
      `;

      section.hidden = false;

      mount.querySelector(
        '[data-implicit-add-related]'
      )?.addEventListener(
        'click',
        (event) => {
          this.#addRelated(
            variant.id,
            event.currentTarget
          );
        },
        { signal: this.#abort.signal }
      );
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.warn(
          '[implicit-cart] Recommendations unavailable',
          error
        );
      }
    }
  }

  async #addRelated(
    variantId,
    button
  ) {
    if (
      !(button instanceof HTMLButtonElement)
    ) {
      return;
    }

    button.disabled = true;
    button.textContent = 'Adding…';

    try {
      const root =
        window.Shopify?.routes?.root || '/';

      const response =
        await fetch(
          `${root}cart/add.js`,
          {
            method: 'POST',
            headers: {
              'Content-Type':
                'application/json',
              Accept:
                'application/json'
            },
            body: JSON.stringify({
              items: [
                {
                  id: variantId,
                  quantity: 1
                }
              ]
            })
          }
        );

      if (!response.ok) {
        throw new Error(
          'Unable to add product'
        );
      }

      await sectionRenderer.renderSection(
        this.dataset.sectionId ||
          'cart-drawer-section',
        {
          cache: false,
          mode: 'hydration'
        }
      );

      /**
       * This custom recommendation Add does not use product-form.js,
       * so it does not emit Horizon's CartLinesUpdateEvent.
       * Keep the already-open drawer open / bring it forward.
       */
      const drawer =
        document.querySelector(
          'theme-drawer#cart-drawer'
        );

      if (
        drawer &&
        typeof drawer.open === 'function'
      ) {
        drawer.open();
      }
    } catch (error) {
      button.disabled = false;
      button.textContent = 'Add';

      console.warn(
        '[implicit-cart] Add related product failed',
        error
      );
    }
  }

  #escape(value) {
    const node =
      document.createElement('div');

    node.textContent =
      String(value ?? '');

    return node.innerHTML;
  }
}

if (!customElements.get('implicit-cart-ui')) {
  customElements.define(
    'implicit-cart-ui',
    ImplicitCartUI
  );
}
